import { CircularProgress, Collapse, Typography } from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import React, { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import { persianRegex } from '../../helpers/regex'
import { getTranslate } from '../../localization'
import {
  ChannelForRateConnection,
  RateConnectionChannelFieldsType,
  RoomTypeForRateConnection
} from '../../types/rateConnection'
import PriceClassDistributionChannelRowMenu from './PriceClassDistributionChannelRowMenu'
import PriceClassDistributionPriceClassRow from './PriceClassDistributionPriceClassRow'
import PriceClassDistributionRateMultiplierRow from './PriceClassDistributionRateMultiplierRow'
import useStyles from './styles/PriceClassDistributionChannelRow.styles'

interface PriceClassDistributionChannelRowProps {
  channelData: ChannelForRateConnection
  openAll: boolean
  roomTypeIndex: number
  ratePlanIndex: number
  channelIndex: number
  updatedRateConnectionList: RoomTypeForRateConnection[]
  setUpdatedRateConnectionList: React.Dispatch<
    React.SetStateAction<RoomTypeForRateConnection[]>
  >
  weekendIndexes: number[]
  range: 'TWO_WEEKS' | 'MONTH'
  selectedMenuFieldInChannelRow: RateConnectionChannelFieldsType
  setSelectedMenuFieldInChannelRow: React.Dispatch<
    React.SetStateAction<RateConnectionChannelFieldsType>
  >
}

const PriceClassDistributionChannelRow: React.FC<
  PriceClassDistributionChannelRowProps
> = ({
  channelData,
  openAll,
  roomTypeIndex,
  ratePlanIndex,
  channelIndex,
  updatedRateConnectionList,
  setUpdatedRateConnectionList,
  weekendIndexes,
  range,
  selectedMenuFieldInChannelRow,
  setSelectedMenuFieldInChannelRow
}) => {
  const classes = useStyles()
  const [showPriceClass, setShowPriceClass] = useState(false)
  const PAGE_SIZE = 10
  const [numberOfVisiblePriceClass, setnumberOfVisiblePriceClass] =
    useState(PAGE_SIZE)

  useEffect(() => {
    if (openAll) {
      setShowPriceClass(true)
    } else {
      setShowPriceClass(false)
    }
  }, [openAll])

  useEffect(() => {
    setShowPriceClass(false)
  }, [range])

  const isPersian = persianRegex.test(channelData.name)

  const menuItems: RateConnectionChannelFieldsType[] = [
    'rateMultiplier',
    'priceClass'
  ]

  return (
    <div>
      <div
        className={[
          classes.channelRow,
          range === 'MONTH' && classes.channelRowForMonth
        ].join(' ')}>
        <div className={classes.rowNameContainer}>
          <Typography
            variant="h4"
            className={classes.rowName}
            style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
            {channelData.name}
          </Typography>{' '}
          <Typography
            variant="h6"
            className={classes.numberOfChannels}
            component="span"
            onClick={() => setShowPriceClass(!showPriceClass)}>
            {selectedMenuFieldInChannelRow === 'priceClass'
              ? `${channelData.priceClasses.length} ${getTranslate(
                  'Price Class'
                )}`
              : `${channelData.rateMultipliers.length} ${getTranslate(
                  'RateMultiplier'
                )}`}
          </Typography>
          <div className={classes.priceClassLoading}>
            <ArrowRightIcon
              className={[
                classes.channelArrowRight,
                showPriceClass ? classes.channelArrowDown : ''
              ].join(' ')}
            />
          </div>
        </div>
        <div className={classes.menuContainer}>
          <PriceClassDistributionChannelRowMenu
            items={menuItems}
            onChangeSelectedItem={(newMenuItem) => {
              setSelectedMenuFieldInChannelRow(newMenuItem)
            }}
            activeItem={selectedMenuFieldInChannelRow}
          />
          <ArrowRightIcon
            className={[
              classes.channelArrowRight,
              classes.channelArrowDown
            ].join(' ')}
          />
        </div>
        {Array(range === 'TWO_WEEKS' ? 14 : 30)
          .fill(' ')
          .map((_, index) => (
            <div
              key={index}
              className={[
                classes.emptyBox,
                weekendIndexes.includes(index % 7)
                  ? classes.weekendDayContainer
                  : ''
              ].join(' ')}
            />
          ))}
      </div>
      <Collapse in={showPriceClass}>
        <div>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => {
              setTimeout(() => {
                setnumberOfVisiblePriceClass(
                  numberOfVisiblePriceClass + PAGE_SIZE
                )
              }, 500)
            }}
            hasMore={
              channelData &&
              numberOfVisiblePriceClass < channelData.priceClasses.length - 1
            }
            loader={
              <div key={0} className={classes.loadingContainer}>
                <CircularProgress size={32} />
              </div>
            }
            useWindow={true}>
            {selectedMenuFieldInChannelRow === 'priceClass'
              ? channelData.priceClasses
                  .slice(0, numberOfVisiblePriceClass)
                  .map((item, priceClassIndex) => (
                    <PriceClassDistributionPriceClassRow
                      key={priceClassIndex}
                      priceClassData={item}
                      roomTypeIndex={roomTypeIndex}
                      ratePlanIndex={ratePlanIndex}
                      channelIndex={channelIndex}
                      priceClassIndex={priceClassIndex}
                      updatedRateConnectionList={updatedRateConnectionList}
                      setUpdatedRateConnectionList={
                        setUpdatedRateConnectionList
                      }
                      weekendIndexes={weekendIndexes}
                      range={range}
                      isPersian={isPersian}
                      selectedMenuFieldInChannelRow={
                        selectedMenuFieldInChannelRow
                      }
                    />
                  ))
              : channelData.rateMultipliers.map((item, rateMultiplierIndex) => (
                  <PriceClassDistributionRateMultiplierRow
                    key={rateMultiplierIndex}
                    rateMultiplierData={item}
                    roomTypeIndex={roomTypeIndex}
                    ratePlanIndex={ratePlanIndex}
                    channelIndex={channelIndex}
                    rateMultiplierIndex={rateMultiplierIndex}
                    updatedRateConnectionList={updatedRateConnectionList}
                    setUpdatedRateConnectionList={setUpdatedRateConnectionList}
                    weekendIndexes={weekendIndexes}
                    range={range}
                    isPersian={isPersian}
                    selectedMenuFieldInChannelRow={
                      selectedMenuFieldInChannelRow
                    }
                  />
                ))}
          </InfiniteScroll>
        </div>
      </Collapse>
    </div>
  )
}

export default PriceClassDistributionChannelRow
